import React, { useState, useEffect, useMemo } from "react";

import { useAuth } from "../core/AuthContext";
import { useSemester } from '../core/SemesterContext';
import Select from "react-select";
import { Icon } from "@iconify/react";
import NavHeader from "./NavHeader";
import { format } from "date-fns";
import DateTimePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from 'react-datepicker';
import { enGB } from 'date-fns/locale';
import "../styles/Form.css";
import "../styles/responsive.css";
import "../styles/edit.css";
import "../styles/OpenCall.css";
import "../styles/AdminStatsManager.css";
import useUsers from './hooks/useUsers';
import CustomAlert from "./CustomAlert";
import CustomAlertWithButtons from "./CustomAlertWithButtons";
import openCallService from "../core/services/OpenCallService";
import eventService from "../core/services/EventService";
import { canAccessAdminStats } from "../core/utils/permissionUtils";


// Register the locale
registerLocale('en-GB', enGB);

function AdminStatsManager() {
    const { loading } = useAuth();
    const authContext = useAuth();
    const contextUser = authContext.user;

    const semesterContext = useSemester();
    const semester = semesterContext.currentSemester;

    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedEventData, setSelectedEventData] = useState(null);
    const [openCallData, setOpenCallData] = useState(null);
    const [openCallFormat, setOpenCallFormat] = useState("shot");
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Form state for editable fields - Event
    const [eventFormData, setEventFormData] = useState({
        mos: [],
        vmos: [],
        participants: []
    });

    // Form state for editable fields - Open Call
    const [openCallFormData, setOpenCallFormData] = useState({
        openCallDeadline: null,
        openCallTime: null,
        numberSpots: '',
        comment: '',
        openCallSpread: '',
        mos: [],
        vmos: [],
        shots: [],
        winners: [],
        status: 'Open'
    });

    const referencedUserIds = useMemo(() => [
        ...(eventFormData.mos || []),
        ...(eventFormData.vmos || []),
        ...(eventFormData.participants || []),
        ...(openCallFormData.mos || []),
        ...(openCallFormData.vmos || []),
        ...(openCallFormData.shots || []),
        ...(openCallFormData.winners || []),
    ], [eventFormData, openCallFormData]);

    const { optionsForSelection, loading: usersLoading } = useUsers({
        resolveIds: referencedUserIds,
    });

    const [alertInfo, setAlertInfo] = useState({
        show: false,
        type: '',
        message: ''
    });

    const [alertActionInfo, setAlertActionInfo] = useState({
        show: false,
        title: '',
        type: '',
        message: '',
        btn1Action: () => { },
        btn2Action: () => { },
        btn1Text: '',
        btn2Text: ''
    });

    // Fetch all events for the current semester
    useEffect(() => {
        const fetchEvents = async () => {
            if (!semester?._id) return;

            try {
                setEventsLoading(true);
                // Admins still need to manage staff on General Meetings even though
                // those are excluded from regular event lists/stats everywhere else.
                const response = await eventService.getEventsList(semester._id, { includeGeneralMeetings: true });
                setEvents(response || []);
            } catch (err) {
                console.error("Error fetching events:", err);
                setAlertInfo({
                    show: true,
                    type: 'error',
                    message: 'Error loading events. Please try again.'
                });
            } finally {
                setEventsLoading(false);
            }
        };
        fetchEvents();
    }, [semester?._id]);

    // Handle format change - update deadline when switching to spread format
    useEffect(() => {
        // Only update if we have event data and no existing open call data
        if (!selectedEventData || openCallData) return;

        if (openCallFormat === "spread" && selectedEventData.date) {
            // When switching to spread format, set deadline to event date + 24 hours
            const defaultDeadline = new Date(new Date(selectedEventData.date).getTime() + 24 * 60 * 60 * 1000);
            setOpenCallFormData(prev => ({
                ...prev,
                openCallDeadline: defaultDeadline,
                openCallTime: defaultDeadline
            }));
        } else if (openCallFormat === "shot") {
            // When switching to shot format, clear the deadline
            setOpenCallFormData(prev => ({
                ...prev,
                openCallDeadline: null,
                openCallTime: null
            }));
        }
    }, [openCallFormat, selectedEventData, openCallData]);

    // Helper function to safely extract ID from object or string
    const extractId = (item) => {
        if (!item) return null;
        return typeof item === 'object' ? item._id : item;
    };

    // Helper function to safely extract array of IDs
    const extractIds = (items) => {
        if (!items || !Array.isArray(items)) return [];
        return items.map(item => extractId(item)).filter(Boolean);
    };

    // Helper function to extract shot user IDs
    const extractShotUserIds = (shots) => {
        if (!shots || !Array.isArray(shots)) return [];
        return shots.map(shot => extractId(shot.user)).filter(Boolean);
    };

    // Create options for the event dropdown
    const eventOptions = events.map(event => ({
        value: event._id,
        label: `${event.name} - ${event.date ? format(new Date(event.date), 'dd/MM/yyyy') : 'No date'}`,
        eventData: event
    }));

    // Refetch open call data
    const refetchOpenCallData = async () => {
        if (!selectedEvent) return;

        try {
            const openCallResponse = await openCallService.getOCByEventId(selectedEvent.value);
            const ocData = openCallResponse;
            setOpenCallData(ocData);

            // Update form data with fresh data
            setOpenCallFormData(prevData => ({
                ...prevData,
                shots: extractShotUserIds(ocData.shots),
                winners: extractIds(ocData.winners)
            }));
        } catch (error) {
            console.error("Error refetching open call data:", error);
        }
    };

    // Handle event selection
    const handleEventSelect = async (selectedOption) => {
        setSelectedEvent(selectedOption);

        if (!selectedOption) {
            setSelectedEventData(null);
            setOpenCallData(null);
            setEventFormData({
                mos: [],
                vmos: [],
                participants: []
            });
            setOpenCallFormData({
                openCallDeadline: null,
                openCallTime: null,
                numberSpots: '',
                comment: '',
                openCallSpread: '',
                mos: [],
                vmos: [],
                shots: [],
                winners: []
            });
            return;
        }

        try {
            // Fetch detailed event data with populated fields
            const eventData = await eventService.getEventById(selectedOption.value);
            setSelectedEventData(eventData);

            // Initialize event form data with event values
            setEventFormData({
                mos: extractIds(eventData.mos),
                vmos: extractIds(eventData.vmos),
                participants: extractIds(eventData.participants)
            });

            // Fetch Open Call data
            try {
                const response = await openCallService.getOCByEventId(eventData._id);
                const ocData = response;
                setOpenCallData(ocData);

                // Determine format
                if (eventData.openCallSpread || ocData.openCallSpread) {
                    setOpenCallFormat("spread");
                } else {
                    setOpenCallFormat("shot");
                }

                // Initialize open call form data
                setOpenCallFormData({
                    openCallDeadline: ocData.openCallDeadline ? new Date(ocData.openCallDeadline) : null,
                    openCallTime: ocData.openCallDeadline ? new Date(ocData.openCallDeadline) : null,
                    numberSpots: ocData.numberSpots || '',
                    comment: ocData.comment || '',
                    openCallSpread: ocData.openCallSpread || eventData.openCallSpread || '',
                    mos: extractIds(ocData.mos || eventData.mos),
                    vmos: extractIds(ocData.vmos || eventData.vmos),
                    shots: extractShotUserIds(ocData.shots),
                    winners: extractIds(ocData.winners),
                    status: ocData.status || 'Open'
                });
            } catch (openCallError) {
                // If no openCallData is found (Error 404)
                setOpenCallData(null);
                const format = eventData.openCallSpread ? "spread" : "shot";
                setOpenCallFormat(format);

                // Use event data as defaults
                // For spread format: default to event date + 24 hours
                // For shot format: default to null (user must set it manually)
                const defaultDeadline = format === "spread" && eventData.date
                    ? new Date(new Date(eventData.date).getTime() + 24 * 60 * 60 * 1000)
                    : null;

                setOpenCallFormData({
                    openCallDeadline: defaultDeadline,
                    openCallTime: defaultDeadline,
                    numberSpots: '',
                    comment: '',
                    openCallSpread: eventData.openCallSpread || '',
                    mos: extractIds(eventData.mos),
                    vmos: extractIds(eventData.vmos),
                    shots: [],
                    winners: [],
                    status: 'Open'
                });
            }
        } catch (err) {
            console.error("Error fetching event details:", err);
            setAlertInfo({
                show: true,
                type: 'error',
                message: 'Error loading event details. Please try again.'
            });
        }
    };

    // Handle adding/removing shots
    const handleShotsChange = async (selectedOptions) => {
        if (!openCallData || !openCallData._id) {
            setAlertInfo({
                show: true,
                type: 'warning',
                message: 'Please create the open call first before managing shots.'
            });
            return;
        }

        const newSelectedUsers = selectedOptions ? selectedOptions.map(option => option.value) : [];
        const currentShots = openCallFormData.shots;

        // Find users to add (in newSelectedUsers but not in currentShots)
        const usersToAdd = newSelectedUsers.filter(userId => !currentShots.includes(userId));

        // Find users to remove (in currentShots but not in newSelectedUsers)
        const usersToRemove = currentShots.filter(userId => !newSelectedUsers.includes(userId));

        try {
            // Send shots for new users
            for (const userId of usersToAdd) {
                await openCallService.sendShot(openCallData._id, userId, '');
            }

            // Cancel shots for removed users
            for (const userId of usersToRemove) {
                await openCallService.cancelShot(openCallData._id, userId);
            }

            // Update local state
            setOpenCallFormData({ ...openCallFormData, shots: newSelectedUsers });

            // Refetch to get updated data
            await refetchOpenCallData();

            setAlertInfo({
                show: true,
                type: 'success',
                message: 'Shots updated successfully!'
            });
        } catch (error) {
            console.error("Error updating shots:", error);
            setAlertInfo({
                show: true,
                type: 'error',
                message: 'Error updating shots. Please try again.'
            });
        }
    };

    // Handle winners change
    const handleWinnersChange = async (selectedOptions) => {
        const newWinners = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setOpenCallFormData({ ...openCallFormData, winners: newWinners });
    };

    // Handle save event
    const handleSaveEvent = async () => {
        setIsSaving(true);

        try {
            const updatePayload = {
                mos: eventFormData.mos,
                vmos: eventFormData.vmos,
                participants: eventFormData.participants
            };

            await eventService.updateEvent(selectedEventData._id, updatePayload);

            setAlertInfo({
                show: true,
                type: 'success',
                message: 'Event updated successfully!'
            });

            // Refetch event data
            const updatedEvent = await eventService.getEventById(selectedEventData._id);
            setSelectedEventData(updatedEvent);
        } catch (error) {
            console.error("Error updating event:", error);
            setAlertInfo({
                show: true,
                type: 'error',
                message: `Error updating event: ${error.message}. Please try again.`
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Handle save/create open call
    const handleSaveOpenCall = async () => {
        // Validation
        if (!openCallFormData.openCallDeadline) {
            setAlertInfo({
                show: true,
                type: 'warning',
                message: 'Open Call Deadline is required.'
            });
            return;
        }

        if (!openCallFormData.comment || openCallFormData.comment.trim() === '') {
            setAlertInfo({
                show: true,
                type: 'warning',
                message: 'Comment is required.'
            });
            return;
        }

        if (openCallFormat === "shot" && !openCallFormData.numberSpots) {
            setAlertInfo({
                show: true,
                type: 'error',
                message: 'Number of spots is required for shot format.'
            });
            return;
        }

        if (openCallFormat === "spread" && !openCallFormData.openCallSpread) {
            setAlertInfo({
                show: true,
                type: 'error',
                message: 'Open Call Spread link is required for spread format.'
            });
            return;
        }

        setIsSaving(true);

        try {
            // Combine date and time
            let finalDate;
            if (openCallFormat === "spread") {
                finalDate = selectedEventData.date
                    ? new Date(new Date(selectedEventData.date).getTime() + 24 * 60 * 60 * 1000)
                    : openCallFormData.openCallDeadline;
            } else {
                finalDate = openCallFormData.openCallDeadline;
                if (openCallFormData.openCallTime) {
                    finalDate = new Date(openCallFormData.openCallDeadline);
                    finalDate.setHours(openCallFormData.openCallTime.getHours(), openCallFormData.openCallTime.getMinutes());
                }
            }

            const openCallPayload = {
                openCallName: selectedEventData.name,
                event: selectedEventData._id,
                openCallDeadline: finalDate,
                numberSpots: openCallFormat === "shot" ? openCallFormData.numberSpots : undefined,
                openCallSpread: openCallFormat === "spread" ? openCallFormData.openCallSpread : undefined,
                mos: openCallFormData.mos || eventFormData.mos || [],
                vmos: openCallFormData.vmos || eventFormData.vmos || [],
                comment: openCallFormData.comment,
                type: selectedEventData.type,
                level: selectedEventData.level,
                semester: semester._id,
                status: openCallFormData.status,
            };

            let currentOpenCallId;

            if (!openCallData || !openCallData._id) {
                // Create new open call
                const response = await openCallService.createOCAdmin(openCallPayload);
                currentOpenCallId = response.openCall._id;
                setOpenCallData(response.openCall);
            } else {
                // Update existing open call
                await openCallService.editOC(openCallData._id, openCallPayload);
                currentOpenCallId = openCallData._id;
            }

            // Save winners if there are any selected
            await openCallService.saveWinners(currentOpenCallId, openCallFormData.winners, false);
            if (openCallFormData.winners && openCallFormData.winners.length > 0) {
                await eventService.addParticipants(selectedEventData._id, openCallFormData.winners);
            }

            setAlertInfo({
                show: true,
                type: 'success',
                message: !openCallData || !openCallData._id
                    ? 'Open Call created successfully!'
                    : 'Open Call updated successfully!'
            });

            // Refetch open call and event data
            await refetchOpenCallData();
            await handleEventSelect(selectedEvent);

        } catch (error) {
            console.error("Error saving open call:", error);
            setAlertInfo({
                show: true,
                type: 'error',
                message: `Error saving open call: ${error.response?.message || error.message}. Please try again.`
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteOpenCall = () => {
        if (!openCallData || !openCallData._id) {
            setAlertInfo({
                show: true,
                type: 'warning',
                message: 'No open call to delete.'
            });
            return;
        }

        // Show confirmation alert with CustomAlertWithButtons
        setAlertActionInfo({
            show: true,
            title: 'Delete Open Call?',
            type: 'error',
            message: 'Are you sure you want to delete this open call? This action cannot be undone.',
            btn1Action: () => {
                // Just close the alert without deleting
                setAlertActionInfo((prevState) => ({
                    ...prevState,
                    show: false,
                }));
            },
            btn2Action: async () => {
                // Close the alert and proceed with deletion
                setAlertActionInfo((prevState) => ({
                    ...prevState,
                    show: false,
                }));

                setIsDeleting(true);

                try {
                    await openCallService.deleteOC(openCallData._id);

                    setAlertInfo({
                        show: true,
                        type: 'success',
                        message: 'Open Call deleted successfully!'
                    });

                    // Reset open call data and refetch event
                    setOpenCallData(null);
                    await handleEventSelect(selectedEvent);

                } catch (error) {
                    console.error("Error deleting open call:", error);
                    setAlertInfo({
                        show: true,
                        type: 'error',
                        message: `Error deleting open call: ${error.message}. Please try again.`
                    });
                } finally {
                    setIsDeleting(false);
                }
            },
            btn1Text: 'Cancel',
            btn2Text: 'Delete',
        });
    };

    // Check permissions
    const canView = canAccessAdminStats(contextUser);



    if (!canView) {
        return (
            <div>
                <div>You do not have permission to view this page.</div>
                <div>
                    <p>Your details:</p>
                    <ul>
                        <li>Email: {contextUser?.email || 'Email not available'}</li>
                        <li>Name: {contextUser?.name || 'Name not available'}</li>
                        <li>Login active: {contextUser?.loginActive === true ? 'Yes' : 'No'}</li>
                    </ul>
                </div>
            </div>
        );
    }

    // Wait for data to load
    if (loading || usersLoading || eventsLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="page-container admin-stats-manager-container">
            <NavHeader />

            <h1 className="page-title">Admin Stats Manager</h1>

            <div className="admin-stats-manager-content">

                {/* Event Selection Dropdown */}
                <div className="event-selector-container">
                    <label>Select Event</label>
                    <Select
                        options={eventOptions}
                        value={selectedEvent}
                        onChange={handleEventSelect}
                        placeholder="Choose an event..."
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>

                {/* Event Details Display */}
                {selectedEventData && (
                    <>
                        <div className="event-details-section">
                            <h2 className="event-details-title">
                                <Icon icon="material-symbols:calendar-month-outline" className="title-icon" />
                                Event Details
                            </h2>

                            {/* Event Name */}
                            <div className="event-name-field">
                                <label className="event-name-label">Event Name:</label>
                                <p className="event-name-display">
                                    {selectedEventData.name || 'N/A'}
                                </p>
                            </div>

                            {/* Additional Event Info */}
                            <div className="event-info-grid">
                                <div className="event-info-grid-container">
                                    <div>
                                        <label className="event-info-label">Date:</label>
                                        <p className="event-info-value">
                                            {selectedEventData.date ? format(new Date(selectedEventData.date), 'dd/MM/yyyy HH:mm') : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="event-info-label">Departments:</label>
                                        <p className="event-info-value">{
                                          Array.isArray(selectedEventData.departments)
                                            ? selectedEventData.departments.map((d) => typeof d === 'object' ? d.name : d).join(', ') || 'N/A'
                                            : selectedEventData.departments || 'N/A'
                                        }</p>
                                    </div>
                                </div>
                            </div>

                            {/* MOs (Main Organizers) - Multi-select Dropdown */}
                            <div className="form-field">
                                <label>
                                    Main Organizers (MOs)
                                    <span>
                                        ({eventFormData.mos?.length || 0})
                                    </span>
                                </label>
                                <Select
                                    isMulti
                                    isSearchable
                                    name="mos"
                                    options={optionsForSelection(eventFormData.mos)}
                                    value={optionsForSelection(eventFormData.mos).filter(option => eventFormData.mos.includes(option.value))}
                                    onChange={(selectedOptions) => {
                                        const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                        setEventFormData({ ...eventFormData, mos: selectedValues });
                                    }}
                                    className="edit-input edit-input-select"
                                    isClearable
                                    isLoading={usersLoading}
                                    placeholder="Select MOs..."
                                />
                            </div>


                            {/* VMOs (Vice Main Organizers) - Multi-select Dropdown */}
                            <div className="form-field">
                                <label>
                                    Vice Main Organizers (VMOs)
                                    <span>
                                        ({eventFormData.vmos?.length || 0})
                                    </span>
                                </label>
                                <Select
                                    isMulti
                                    isSearchable
                                    name="vmos"
                                    options={optionsForSelection(eventFormData.vmos)}
                                    value={optionsForSelection(eventFormData.vmos).filter(option => eventFormData.vmos.includes(option.value))}
                                    onChange={(selectedOptions) => {
                                        const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                        setEventFormData({ ...eventFormData, vmos: selectedValues });
                                    }}
                                    className="edit-input edit-input-select"
                                    isClearable
                                    isLoading={usersLoading}
                                    placeholder="Select VMOs..."
                                />
                            </div>

                            {/* Participants - Multi-select Dropdown */}
                            <div className="form-field">
                                <label>
                                    Participants
                                    <span>
                                        ({eventFormData.participants?.length || 0})
                                    </span>
                                </label>
                                <Select
                                    isMulti
                                    isSearchable
                                    name="participants"
                                    options={optionsForSelection(eventFormData.participants)}
                                    value={optionsForSelection(eventFormData.participants).filter(option => eventFormData.participants.includes(option.value))}
                                    onChange={(selectedOptions) => {
                                        const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                        setEventFormData({ ...eventFormData, participants: selectedValues });
                                    }}
                                    className="edit-input edit-input-select"
                                    isClearable
                                    isLoading={usersLoading}
                                    placeholder="Select participants..."
                                />
                            </div>

                            {/* Save Event Button */}
                            <div className="button-container-right">
                                <button
                                    type="button"
                                    onClick={handleSaveEvent}
                                    disabled={isSaving}
                                    className="button button-primary save-button"
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>

                        </div>

                        {/* Open Call Details Section */}
                        <div className="open-call-section">
                            <h2 className="open-call-title">
                                <Icon icon="bxs:megaphone" className="title-icon" />
                                Open Call Information
                            </h2>

                            {openCallData === null && (
                                <div className="open-call-no-data">
                                    <p>
                                        <Icon icon="material-symbols:info-outline" className="open-call-no-data-icon" />
                                        No open call found for this event - Fill the form below to create one
                                    </p>
                                </div>
                            )}

                            {/* Open Call Format Toggle */}
                            <div className="open-call-format-toggle">
                                <button
                                    type="button"
                                    className={`open-call-format-toggle-btn ${openCallFormat === "shot" ? "active" : ""}`}
                                    onClick={() => setOpenCallFormat("shot")}
                                >
                                    <Icon icon="nimbus:shot" className='icon' style={{ marginRight: 8, width: 18, height: 18 }} /> Shot
                                </button>
                                <button
                                    type="button"
                                    className={`open-call-format-toggle-btn ${openCallFormat === "spread" ? "active" : ""}`}
                                    onClick={() => setOpenCallFormat("spread")}
                                >
                                    <Icon icon="garden:file-spreadsheet-fill-12" style={{ marginRight: 8, width: 14, height: 14 }} /> Spread
                                </button>
                            </div>

                            {/* Open Call Deadline */}
                            <div className="form-field">
                                <label>Open Call Deadline *</label>
                                <div className="date-time-container">
                                    <DateTimePicker
                                        selected={openCallFormData.openCallDeadline}
                                        onChange={(date) => setOpenCallFormData({ ...openCallFormData, openCallDeadline: date })}
                                        showTimeSelect={false}
                                        dateFormat="dd/MM/yyyy"
                                        locale="en-GB"
                                        placeholderText={new Date().toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                        })}
                                        className="edit-input"
                                        wrapperClassName="custom-datepicker-wrapper"
                                    />

                                    <DateTimePicker
                                        selected={openCallFormData.openCallTime}
                                        onChange={(time) => setOpenCallFormData({ ...openCallFormData, openCallTime: time })}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={5}
                                        timeCaption="Time"
                                        dateFormat="HH:mm"
                                        locale="en-GB"
                                        placeholderText="00:00"
                                        className="edit-input"
                                        wrapperClassName="custom-timepicker-wrapper"
                                    />
                                </div>
                                {openCallFormat === "spread" && (
                                    <small className="date-time-note">
                                        Deadline for spreadsheets is automatically set to the date of the event plus one day. Change if needed.
                                    </small>
                                )}
                            </div>

                            {/* Shot Format Fields */}
                            {openCallFormat === "shot" && (
                                <div className="form-field">
                                    <label>Number of Spots *</label>
                                    <input
                                        type="number"
                                        name="spots"
                                        onChange={(e) => setOpenCallFormData({ ...openCallFormData, numberSpots: e.target.value })}
                                        value={openCallFormData.numberSpots}
                                        placeholder="0"
                                        className="edit-input"
                                        step="1"
                                    />
                                </div>
                            )}

                            {/* Spread Format Fields */}
                            {openCallFormat === "spread" && (
                                <div className="form-field">
                                    <label>Open Call Spread Link *</label>
                                    <input
                                        type="text"
                                        name="openCallSpread"
                                        onChange={(e) => setOpenCallFormData({ ...openCallFormData, openCallSpread: e.target.value })}
                                        value={openCallFormData.openCallSpread}
                                        placeholder="Paste the Open Call Spread link here"
                                        className="edit-input"
                                    />
                                </div>
                            )}

                            {/* Comment */}
                            <div className="form-field">
                                <label>Comment *</label>
                                <textarea
                                    name="comment"
                                    onChange={(e) => setOpenCallFormData({ ...openCallFormData, comment: e.target.value })}
                                    value={openCallFormData.comment}
                                    placeholder="Add information like: place of the event, date, name of the OC and more..."
                                    className="comment-input comment-textarea"
                                />
                            </div>

                            {openCallData && openCallFormat === "shot" && (
                                <div id="shots-section" className="shots-winners-section">
                                    <label>
                                        <Icon icon="nimbus:shot" className="shots-winners-label-icon" />
                                        Shots

                                        <span>
                                            ({openCallFormData.shots?.length || 0})
                                        </span>
                                    </label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        name="shots"
                                        options={optionsForSelection(openCallFormData.shots)}
                                        value={optionsForSelection(openCallFormData.shots).filter(option => openCallFormData.shots.includes(option.value))}
                                        onChange={handleShotsChange}
                                        className="edit-input edit-input-select"
                                        isClearable
                                        isLoading={usersLoading}
                                        placeholder="Select users who sent shots..."
                                        isDisabled={!openCallData}
                                    />
                                    {!openCallData && (
                                        <small className="field-note">
                                            Create the open call first to manage shots
                                        </small>
                                    )}
                                </div>
                            )}

                            {openCallData && openCallFormat === "shot" && (
                                <div className="shots-winners-section">
                                    <label>
                                        <Icon icon="mdi:prize" className="shots-winners-label-icon" />
                                        Winners

                                        <span>
                                            ({openCallFormData.winners?.length || 0})
                                        </span>
                                    </label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        name="winners"
                                        options={optionsForSelection(openCallFormData.shots).filter(option => openCallFormData.shots.includes(option.value))}
                                        value={optionsForSelection(openCallFormData.winners).filter(option => openCallFormData.winners.includes(option.value))}
                                        onChange={handleWinnersChange}
                                        className="edit-input edit-input-select"
                                        isClearable
                                        isLoading={usersLoading}
                                        placeholder="Select winners from shots..."
                                        isDisabled={!openCallData || openCallFormData.shots.length === 0}
                                    />
                                    {!openCallData && (
                                        <small className="field-note">
                                            Create the open call first to select winners
                                        </small>
                                    )}
                                    {openCallData && openCallFormData.shots.length === 0 && (
                                        <small className="field-note">
                                            Add shots first before selecting winners
                                        </small>
                                    )}
                                    {openCallData && openCallFormData.winners && openCallFormData.winners.length > 0 && (
                                        <small className="field-note-info">
                                            Note: Removing users from winners does not remove them from the event participants list (intentional)
                                        </small>
                                    )}
                                </div>
                            )}

                            {/* Status Dropdown */}
                            <div className="shots-winners-section">
                                <label>
                                    <Icon icon="mdi:circle-double" className="shots-winners-label-icon" />
                                    Status:
                                </label>
                                <Select
                                    name="status"
                                    options={[
                                        { value: 'Open', label: 'Open' },
                                        { value: 'On Voting', label: 'On Voting' },
                                        { value: 'Closed', label: 'Closed' }
                                    ]}
                                    value={{
                                        value: openCallFormData.status || 'Open',
                                        label: openCallFormData.status || 'Open'
                                    }}
                                    onChange={(option) => setOpenCallFormData({ ...openCallFormData, status: option.value })}
                                    className="edit-input edit-input-select"
                                    isSearchable={false}
                                    styles={{
                                        singleValue: (provided, state) => ({
                                            ...provided,
                                            fontWeight: 'bold',
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            color: state.data.value === 'Open' ? '#28a745' :
                                                state.data.value === 'On Voting' ? '#ffc107' :
                                                    '#dc3545',
                                            fontWeight: 'bold',
                                        })
                                    }}
                                />
                                {openCallFormData.status === 'On Voting' && (
                                    <small className="field-note-info">
                                        Note: "On Voting" status only applies if the deadline has passed
                                    </small>
                                )}
                            </div>

                            {/* Open Call Buttons */}
                            <div className="button-container-right">
                                {openCallData && openCallData._id && (
                                    <button
                                        onClick={handleDeleteOpenCall}
                                        disabled={isDeleting}
                                        className={`button button-primary open-call-delete-button ${isDeleting ? '' : ''}`}
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleSaveOpenCall}
                                    disabled={isSaving}
                                    className="button button-primary open-call-save-button"
                                >
                                    {isSaving ? 'Saving...' : (openCallData ? 'Save' : 'Create')}
                                </button>
                            </div>

                        </div>
                    </>
                )}

                {!selectedEventData && !selectedEvent && (
                    <div className="empty-state">
                        <Icon icon="material-symbols:info-outline" className="empty-state-icon" />
                        <p>Please select an event from the dropdown above to view its details.</p>
                    </div>
                )}
            </div>

            {/* Custom Alert */}
            {alertInfo.show && (
                <CustomAlert
                    type={alertInfo.type}
                    message={alertInfo.message}
                    timeout={4000}
                    onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
                />
            )}

            {alertActionInfo.show && (
                <CustomAlertWithButtons
                    title={alertActionInfo.title}
                    type={alertActionInfo.type}
                    message={alertActionInfo.message}
                    onButton1Click={alertActionInfo.btn1Action}
                    onButton2Click={alertActionInfo.btn2Action}
                    button1Text={alertActionInfo.btn1Text}
                    button2Text={alertActionInfo.btn2Text}
                />
            )}
        </div>
    );
}

export default AdminStatsManager;
